import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/layout';
import { Toast, SearchableSelect } from '../../components';
import { carService, voyageDropdownService, VoyageDropdownItem } from '../../services';
import { useToast } from '../../hooks/useToast';
import * as XLSX from 'xlsx';

interface ChassisLengthEntry {
  chassis: string;
  length: number;
}

interface FailedCar {
  chassis: string;
  reason: string;
}

const BulkLineMeterPage: React.FC = () => {
  const navigate = useNavigate();
  const { toast, showToast, hideToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [voyages, setVoyages] = useState<VoyageDropdownItem[]>([]);
  const [sourceVoyage, setSourceVoyage] = useState<VoyageDropdownItem | null>(null);
  const [entries, setEntries] = useState<ChassisLengthEntry[]>([]);
  const [fileName, setFileName] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [parseError, setParseError] = useState<string>('');
  const [failedCars, setFailedCars] = useState<FailedCar[]>([]);
  const [showFailedModal, setShowFailedModal] = useState(false);
  const [lastResult, setLastResult] = useState<{ updated: number; failed: number } | null>(null);
  const [showBulkAddModal, setShowBulkAddModal] = useState(false);
  const [bulkAddText, setBulkAddText] = useState('');

  // Load voyages using lightweight dropdown endpoint
  useEffect(() => {
    const loadVoyages = async () => {
      try {
        const data = await voyageDropdownService.getAll({ limit: 1000 });
        setVoyages(data);
      } catch (error: any) {
        console.error('Error loading voyages:', error);
        showToast('Failed to load voyages', 'error');
      }
    };
    loadVoyages();
  }, [showToast]);

  // Handle file upload and parse Excel
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setParseError('');
    setEntries([]);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json<{ [key: string]: any }>(firstSheet, { header: 1 });
      
      // Extract chassis numbers and lengths from first two columns (skip header if present)
      const parsedEntries: ChassisLengthEntry[] = [];
      const errors: string[] = [];
      
      for (let i = 0; i < jsonData.length; i++) {
        const row = jsonData[i] as any[];
        if (row && row[0]) {
          const chassisValue = String(row[0]).trim();
          const lengthValue = row[1];
          
          // Skip header-like values
          if (chassisValue && !chassisValue.toLowerCase().includes('chassis') && chassisValue.length > 0) {
            // Parse length value
            const length = parseFloat(lengthValue);
            if (isNaN(length) || length <= 0) {
              errors.push(`Row ${i + 1}: Invalid length value for chassis "${chassisValue}"`);
              continue;
            }
            parsedEntries.push({ chassis: chassisValue, length });
          }
        }
      }

      if (parsedEntries.length === 0) {
        setParseError('No valid entries found in the Excel file. Make sure chassis numbers are in the first column and lengths (in meters) are in the second column.');
        return;
      }

      if (errors.length > 0 && errors.length <= 5) {
        setParseError(`Warning: ${errors.length} rows skipped due to invalid data. ${errors.slice(0, 3).join('; ')}`);
      } else if (errors.length > 5) {
        setParseError(`Warning: ${errors.length} rows skipped due to invalid data.`);
      }

      setEntries(parsedEntries);
    } catch (error) {
      console.error('Error parsing file:', error);
      setParseError('Failed to parse file. Make sure it is a valid Excel file (.xlsx or .xls).');
    }
  };

  // Handle bulk add from pasted text
  const handleBulkAddSubmit = () => {
    const lines = bulkAddText.split(/\n/).map(s => s.trim()).filter(s => s.length > 0);
    if (lines.length === 0) {
      setParseError('No entries found. Enter one entry per line (chassis,length).');
      return;
    }

    const parsedEntries: ChassisLengthEntry[] = [];
    const errors: string[] = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      // Support both comma and tab as delimiters
      const parts = line.split(/[,\t]+/).map(p => p.trim());
      
      if (parts.length < 2) {
        errors.push(`Line ${i + 1}: Missing length value`);
        continue;
      }

      const chassis = parts[0];
      const length = parseFloat(parts[1]);

      if (!chassis) {
        errors.push(`Line ${i + 1}: Missing chassis number`);
        continue;
      }

      if (isNaN(length) || length <= 0) {
        errors.push(`Line ${i + 1}: Invalid length value`);
        continue;
      }

      parsedEntries.push({ chassis, length });
    }

    if (parsedEntries.length === 0) {
      setParseError(`No valid entries found. ${errors.slice(0, 3).join('; ')}`);
      return;
    }

    if (errors.length > 0) {
      setParseError(`Warning: ${errors.length} lines skipped due to invalid data.`);
    } else {
      setParseError('');
    }

    setEntries(parsedEntries);
    setFileName('Manual entry');
    setBulkAddText('');
    setShowBulkAddModal(false);
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!sourceVoyage) {
      showToast('Please select a source voyage', 'error');
      return;
    }
    if (entries.length === 0) {
      showToast('Please upload an Excel file or add entries manually', 'error');
      return;
    }

    setSubmitting(true);
    setFailedCars([]);
    setLastResult(null);
    
    try {
      const result = await carService.bulkUpdateLengthByChassis(sourceVoyage.id, entries) as { 
        message: string; 
        updated_count: number; 
        failed?: FailedCar[] 
      };
      
      const failed = result.failed || [];
      setLastResult({ updated: result.updated_count, failed: failed.length });
      
      if (failed.length > 0) {
        setFailedCars(failed);
        setShowFailedModal(true);
        showToast(`Updated ${result.updated_count} cars. ${failed.length} failed.`, failed.length > 0 ? 'error' : 'success');
      } else {
        showToast(result.message, 'success');
      }
      
      // Reset form
      setEntries([]);
      setFileName('');
      setSourceVoyage(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error: any) {
      console.error('Error updating length:', error);
      const errorMessage = error?.response?.data?.detail || 'Failed to update length';
      showToast(errorMessage, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Build voyage options for React Select
  const voyageOptions = voyages.map(v => ({
    value: v.id,
    label: `${v.voyage_code} - ${v.vessel_description}`
  }));

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 transition-colors mb-4 flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            Bulk Add Line Meter
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Upload an Excel file or enter data manually to update the length (meters) for multiple vehicles by chassis number within a specific voyage.
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          {/* File Upload */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Upload Excel File (Chassis Number in Column A, Length in Column B)
            </label>
            <div className="flex items-center gap-4">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="block w-full text-sm text-gray-500 dark:text-gray-400
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-medium
                  file:bg-blue-50 file:text-blue-700
                  hover:file:bg-blue-100
                  dark:file:bg-gray-700 dark:file:text-gray-300
                  dark:hover:file:bg-gray-600"
              />
              <button
                type="button"
                onClick={() => setShowBulkAddModal(true)}
                className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 whitespace-nowrap"
              >
                Bulk Add
              </button>
            </div>
            {parseError && (
              <p className={`mt-2 text-sm ${parseError.startsWith('Warning') ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'}`}>
                {parseError}
              </p>
            )}
            {fileName && entries.length > 0 && (
              <p className="mt-2 text-sm text-green-600 dark:text-green-400">
                Found {entries.length} valid entries in "{fileName}"
              </p>
            )}
          </div>

          {/* Preview entries */}
          {entries.length > 0 && (
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-900 rounded-md border border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Entries Preview ({entries.length} total)
              </h3>
              <div className="max-h-60 overflow-y-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-100 dark:bg-gray-800">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-300">Chassis Number</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-300">Length (m)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {entries.slice(0, 50).map((entry, idx) => (
                      <tr key={idx} className="bg-white dark:bg-gray-800">
                        <td className="px-3 py-2 font-mono text-gray-900 dark:text-white">
                          {entry.chassis}
                        </td>
                        <td className="px-3 py-2 text-gray-900 dark:text-white">
                          {entry.length.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {entries.length > 50 && (
                  <p className="mt-2 text-xs text-gray-500 text-center">
                    ...and {entries.length - 50} more entries
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Voyage Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Source Voyage
            </label>
            <SearchableSelect
              options={voyageOptions}
              value={sourceVoyage?.id || ''}
              onChange={(value) => setSourceVoyage(voyages.find(v => v.id === value) || null)}
              placeholder="Select source voyage..."
            />
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              onClick={handleSubmit}
              disabled={submitting || !sourceVoyage || entries.length === 0}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? 'Processing...' : `Update ${entries.length} Vehicles`}
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Add Modal */}
      {showBulkAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  Bulk Add Entries
                </h2>
                <button
                  onClick={() => setShowBulkAddModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Paste entries below (one per line, format: chassis,length or chassis{'\t'}length)
              </p>
            </div>
            <div className="p-6">
              <textarea
                value={bulkAddText}
                onChange={(e) => setBulkAddText(e.target.value)}
                placeholder={`CHASSIS001,5.2\nCHASSIS002,4.8\nCHASSIS003,6.1`}
                rows={10}
                className="w-full px-3 py-2 text-sm font-mono border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              />
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Use comma or tab to separate chassis number and length in meters
              </p>
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => setShowBulkAddModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkAddSubmit}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Add Entries
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Failed Cars Modal */}
      {showFailedModal && failedCars.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  Operation Result
                </h2>
                <button
                  onClick={() => setShowFailedModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              {lastResult && (
                <div className="mt-2 flex gap-4">
                  <span className="text-green-600 dark:text-green-400">
                    ✓ {lastResult.updated} updated successfully
                  </span>
                  <span className="text-red-600 dark:text-red-400">
                    ✗ {lastResult.failed} failed
                  </span>
                </div>
              )}
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Failed Entries:
              </h3>
              <div className="space-y-2">
                {failedCars.map((item, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md"
                  >
                    <span className="font-mono text-sm text-gray-900 dark:text-gray-100">
                      {item.chassis}
                    </span>
                    <span className="text-sm text-red-600 dark:text-red-400">
                      {item.reason}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => {
                  const text = failedCars.map((item) => item.chassis).join('\n');
                  navigator.clipboard.writeText(text);
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                Copy Failed
              </button>
              <button
                onClick={() => setShowFailedModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={hideToast}
        />
      )}
    </Layout>
  );
};

export default BulkLineMeterPage;
